package dev.lingvo.ops.state

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.ops.api.OpsApiException
import dev.lingvo.shared.ops.OpsIdentityDto
import dev.lingvo.site.api.SessionRequestIdentity
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/** A session generation, never an email or token, owns one operator verification. */
sealed interface OpsShellSession {
    data object Checking : OpsShellSession
    data object SignedOut : OpsShellSession
    data object Unreachable : OpsShellSession
    data class Authenticated(val identity: SessionRequestIdentity) : OpsShellSession
}

data class OpsShellRequest(val owner: OpsShellSession.Authenticated, val attempt: Long)

data class OpsShellState(
    val session: OpsShellSession,
    val guard: OpsGuard = initialGuard(session),
    val identity: OpsIdentityDto? = null,
    val failure: OpsApiException? = null,
    val request: OpsShellRequest? = null,
    val attempt: Long = 0,
    val query: String = "",
    val menuOpen: Boolean = false,
)

sealed interface OpsShellIntent {
    data object Verify : OpsShellIntent
    data object Retry : OpsShellIntent
    data class SessionChanged(val session: OpsShellSession) : OpsShellIntent
    data class Verified(val request: OpsShellRequest, val identity: OpsIdentityDto) : OpsShellIntent
    data class Failed(val request: OpsShellRequest, val failure: OpsApiException) : OpsShellIntent
    data class QueryChanged(val value: String) : OpsShellIntent
    data class MenuChanged(val open: Boolean) : OpsShellIntent
    data object Navigated : OpsShellIntent
}

/** Pure transitions: retiring authority always removes the old identity and protected content. */
fun reduceOpsShell(state: OpsShellState, intent: OpsShellIntent): OpsShellState = when (intent) {
    is OpsShellIntent.SessionChanged -> if (state.session == intent.session) state
        else OpsShellState(intent.session, attempt = state.attempt + 1)
    OpsShellIntent.Verify -> if (state.request != null || state.guard == OpsGuard.ALLOWED) state else beginVerification(state)
    OpsShellIntent.Retry -> beginVerification(state)
    is OpsShellIntent.Verified -> if (!state.accepts(intent.request)) state else state.copy(
        guard = OpsGuard.ALLOWED, identity = intent.identity, failure = null, request = null,
    )
    is OpsShellIntent.Failed -> if (!state.accepts(intent.request)) state else state.copy(
        guard = when (intent.failure.status) {
            401 -> OpsGuard.SIGNED_OUT
            404 -> OpsGuard.NOT_AN_OPERATOR
            else -> OpsGuard.UNREACHABLE
        },
        identity = null, failure = intent.failure, request = null,
    )
    is OpsShellIntent.QueryChanged -> state.copy(query = intent.value)
    is OpsShellIntent.MenuChanged -> state.copy(menuOpen = intent.open)
    OpsShellIntent.Navigated -> state.copy(menuOpen = false)
}

private fun beginVerification(state: OpsShellState): OpsShellState {
    val owner = state.session as? OpsShellSession.Authenticated ?: return state
    val attempt = state.attempt + 1
    return state.copy(guard = OpsGuard.CHECKING, identity = null, failure = null,
        attempt = attempt, request = OpsShellRequest(owner, attempt))
}

private fun OpsShellState.accepts(candidate: OpsShellRequest): Boolean =
    request == candidate && session == candidate.owner

private fun initialGuard(session: OpsShellSession): OpsGuard = when (session) {
    OpsShellSession.Checking, is OpsShellSession.Authenticated -> OpsGuard.CHECKING
    OpsShellSession.SignedOut -> OpsGuard.SIGNED_OUT
    OpsShellSession.Unreachable -> OpsGuard.UNREACHABLE
}

/** One effect owner. Late responses cannot publish across a logout, refresh, retry or disposal. */
class OpsShellStore(
    initialSession: OpsShellSession,
    private val scope: CoroutineScope,
    private val currentSession: () -> OpsShellSession,
    private val readIdentity: suspend () -> OpsIdentityDto,
) {
    var state by mutableStateOf(OpsShellState(initialSession))
        private set

    private var verification: Job? = null
    private var closed = false

    fun dispatch(intent: OpsShellIntent) {
        if (closed) return
        val previous = state.request
        state = reduceOpsShell(state, intent)
        if (state.request == previous) return
        verification?.cancel()
        val request = state.request ?: return
        verification = scope.launch {
            if (closed || currentSession() != request.owner || !state.accepts(request)) return@launch
            try {
                val identity = readIdentity()
                publish(request, OpsShellIntent.Verified(request, identity))
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                val refusal = failure as? OpsApiException ?: OpsApiException(0, "The operator API is unreachable.")
                publish(request, OpsShellIntent.Failed(request, refusal))
            }
        }
    }

    private fun publish(request: OpsShellRequest, intent: OpsShellIntent) {
        if (!closed && currentSession() == request.owner && state.accepts(request)) {
            // Publication is not a new command: do not cancel the coroutine delivering its result.
            state = reduceOpsShell(state, intent)
        }
    }

    fun close() {
        closed = true
        verification?.cancel()
        state = OpsShellState(OpsShellSession.SignedOut)
    }
}
